import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ComplexOrderInterface } from "../../common/types/types";
import { selectAllSaleItems } from "../../store/slices/saleItemListSlice";
import {
  setComplexOrderCartItems,
  setComplexCartPhone,
  setComplexCartComment,
  setComplexCartFieldValues,
} from "../../store/slices/complexOrderCartSlice";
import { openComplexOrderCartModal } from "../../store/slices/commonModalSlice";
import { useDisplayPrice } from "../../common/hooks/useDisplayPrice";

export const useComplexRepeatOrder = () => {
  const dispatch = useDispatch();
  const allItems = useSelector(selectAllSaleItems);
  const getPrice = useDisplayPrice();

  const repeatOrder = useCallback(
    (order: ComplexOrderInterface) => {
      if (!order.items || order.items.length === 0) return;

      const complexItems = order.items
        .map((orderItem) => {
          const itemId = Number(orderItem.itemId);
          const saleItem = allItems.find((item) => item.id === itemId);

          if (!saleItem) return null;

          const currentPrice = getPrice(saleItem.prices);

          return {
            id: crypto.randomUUID
              ? crypto.randomUUID()
              : `${Date.now()}-${Math.random()}`,
            itemId,
            name: saleItem.name,
            price: currentPrice,
            quantity: orderItem.quantity,
            total: currentPrice * orderItem.quantity,
            image: saleItem.files?.[0],
            categoryId: saleItem.categoryId
              ? Number(saleItem.categoryId)
              : undefined,
            minQuantity: saleItem.minQuantity ?? 1,
            quantityStep: saleItem.quantityStep ?? 1,
            attributes: saleItem.attributes?.reduce(
              (acc, attr) => {
                acc[attr.name] = String(attr.value);
                return acc;
              },
              {} as Record<string, any>,
            ),
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      if (complexItems.length === 0) return;

      dispatch(setComplexOrderCartItems(complexItems));
      dispatch(setComplexCartPhone(order.phone_number || ""));
      dispatch(setComplexCartComment(order.notes || ""));

      if (order.data?.length) {
        dispatch(
          setComplexCartFieldValues(
            order.data.map((d) => ({
              fieldId: d.fieldTemplateId || 0,
              value: d.value,
            })),
          ),
        );
      }

      dispatch(openComplexOrderCartModal());
    },
    [dispatch, allItems, getPrice],
  );

  return { repeatOrder };
};
